import { ref, computed } from 'vue'
import pcsService from '@/services/usePriceComparison'
import type {
	ProductFilterCategory,
	ProductSearchPayload,
	ProductCount,
	ProductGroup,
	LocationSourceCount,
	SearchFilters,
	PriceFilter,
} from '@/services/pcsServiceTypes'

export interface CheckboxItem {
	name: string
	id: string
	selected: boolean
	count: number
}

interface LocationItem {
	name: string
	items: CheckboxItem[]
}

const filterPayload = ref<ProductSearchPayload>({
	keyword: '*',
	filter: {},
	display_currency: 'HKD',
	page: 1,
})

const getSelectedIds = (items: CheckboxItem[]) =>
	items
		.filter(item => item.selected)
		.map(item => item.id)
		.join(',')

const currentFilterOptions = computed(() => {
	const options: Partial<SearchFilters> = {
		brand: getSelectedIds(brandList.value),
		color: getSelectedIds(colorList.value),
		location: getSelectedIds(locationList.value.flatMap(item => item.items)),
		attr: getSelectedIds([
			...osList.value,
			...storageList.value,
			...seriesList.value,
			...manufactList.value,
		]),
		price: {
			gte: minPrice.value,
			lte: maxPrice.value,
		},
	}

	return options as SearchFilters
})

const updateProductData = async (keyword: string) => {
	filterPayload.value.filter = currentFilterOptions.value
	await getProductData(1, keyword)
}

const currentFilterAmount = computed(() => {
	const allCheckboxItems = [
		...brandList.value,
		...colorList.value,
		...osList.value,
		...storageList.value,
		...seriesList.value,
		...manufactList.value,
		...locationList.value.flatMap(item => item.items),
	]

	const selectedCount = allCheckboxItems.filter(item => item.selected).length

	// Check if the price filter has been changed from its default values
	const isPriceChanged =
		minPrice.value !== 0 || maxPrice.value !== defaultMaxPrice.value

	return isPriceChanged ? selectedCount + 1 : selectedCount
})

enum AttributeID {
	OS = '7600',
	STORAGE = '3563',
	SERIES = '2733',
	RAMSIZE = '2688',
}

const checkboxTitles = ref({
	[AttributeID.OS]: '',
	[AttributeID.STORAGE]: '',
	[AttributeID.SERIES]: '',
	[AttributeID.RAMSIZE]: '',
})

const rawFilterData = ref<ProductFilterCategory>()
const brandList = ref<CheckboxItem[]>([])
const locationList = ref<LocationItem[]>([])
const colorList = ref<CheckboxItem[]>([])
const osList = ref<CheckboxItem[]>([])
const storageList = ref<CheckboxItem[]>([])
const seriesList = ref<CheckboxItem[]>([])
const manufactList = ref<CheckboxItem[]>([])
const defaultMaxPrice = ref(20000) // The price is set according to Figma design
const minPrice = ref(0)
const maxPrice = ref(defaultMaxPrice.value)
const totalResults = ref(0)
const isLoading = ref(false)
const isInited = ref(false)

const currentProductList = ref<ProductGroup[]>([])
const isShowMore = ref(false)
const currentPage = ref(1)

const mapFilterOptionsToCheckboxItems = <
	T extends ProductCount | LocationSourceCount,
>(
	items: T[],
	filterKey: keyof Pick<SearchFilters, 'brand' | 'color' | 'location'>,
	nameKey: Extract<keyof T, 'name' | 'source'>,
): CheckboxItem[] => {
	return items
		.filter(item => item[nameKey]) // Filter out items with empty nameKey value
		.map(item => {
			const idKey =
				'search_value' in item
					? ('search_value' as Extract<keyof T, 'search_value'>)
					: ('name' as Extract<keyof T, 'name'>)

			return {
				name: item[nameKey] as string,
				id:
					'search_value' in item
						? (item.search_value as string)
						: (item[nameKey] as string),
				selected:
					filterPayload.value.filter?.[filterKey]
						?.split(',')
						.includes(item[idKey] as string) ?? false,
				count: item.count,
			}
		})
}

const mapAttributesToCheckboxItems = (
	attributeId: AttributeID,
): CheckboxItem[] => {
	const attributeItem = rawFilterData.value?.attributes?.find(
		item => item.id === attributeId,
	)
	if (attributeItem) {
		checkboxTitles.value[attributeId] = attributeItem.name
		const attributeParentId = attributeItem.id

		return attributeItem.value.map(item => {
			// change attribute id to include parent id per backends' request, eg. 7600_549456
			const itemId = `${attributeParentId}_${item.id}`
			return {
				name: item.name,
				id: itemId,
				selected:
					filterPayload.value.filter?.attr?.split(',').includes(itemId) ??
					false,
				count: item.count,
			}
		})
	}
	return []
}

const setFilterData = () => {
	if (!rawFilterData.value) return

	brandList.value = mapFilterOptionsToCheckboxItems(
		rawFilterData.value.brand,
		'brand',
		'name',
	)
	locationList.value = rawFilterData.value.location.map(item => ({
		name: item.name,
		items: mapFilterOptionsToCheckboxItems(item.value, 'location', 'source'),
	}))
	colorList.value = mapFilterOptionsToCheckboxItems(
		rawFilterData.value.color,
		'color',
		'name',
	)

	osList.value = mapAttributesToCheckboxItems(AttributeID.OS)
	storageList.value = mapAttributesToCheckboxItems(AttributeID.STORAGE)
	seriesList.value = mapAttributesToCheckboxItems(AttributeID.SERIES)
	manufactList.value = mapAttributesToCheckboxItems(AttributeID.RAMSIZE)
}

const addPriceToSearchParams = (
	price: PriceFilter,
	searchParams: URLSearchParams,
): void => {
	if (typeof price.gte === 'number') {
		searchParams.set('price_gte', price.gte.toString())
	} else {
		searchParams.delete('price_gte')
	}

	if (typeof price.lte === 'number') {
		searchParams.set('price_lte', price.lte.toString())
	} else {
		searchParams.delete('price_lte')
	}
}

const addFilterOptionsToUrl = () => {
	const filterOptions = currentFilterOptions.value
	const searchParams = new URLSearchParams(window.location.search)

	Object.entries(filterOptions).forEach(([key, value]) => {
		if (key === 'price' && value) {
			addPriceToSearchParams(value, searchParams)
		} else {
			value ? searchParams.set(key, value) : searchParams.delete(key)
		}
	})

	const newUrl = `${window.location.pathname}?${searchParams.toString()}`
	window.history.replaceState(null, '', newUrl)
}

const setFilterPayloadFromUrl = () => {
	const searchParams = new URLSearchParams(window.location.search)
	const newFilter: SearchFilters = {}
	const allowedKeys = [
		'brand',
		'color',
		'location',
		'attr',
		'price_gte',
		'price_lte',
	]

	Array.from(searchParams.entries()).forEach(([key, value]) => {
		if (!allowedKeys.includes(key)) return

		if (key === 'price_gte' || key === 'price_lte') {
			const priceKey = key === 'price_gte' ? 'gte' : 'lte'

			// Only set the price key if the value is a valid number
			if (!isNaN(Number(value))) {
				newFilter.price = newFilter.price || {}
				newFilter.price[priceKey] = Number(value)
			}
		} else {
			// Treat other values as a single string
			newFilter[key as keyof SearchFilters] = value
		}
	})

	filterPayload.value.filter = newFilter
	minPrice.value = newFilter.price?.gte ? Number(newFilter.price.gte) : 0
	maxPrice.value = newFilter.price?.lte
		? Number(newFilter.price.lte)
		: defaultMaxPrice.value
}

const getProductData = async (page: number = 1, keyword: string = '*') => {
	try {
		isLoading.value = true
		filterPayload.value.page = page
		filterPayload.value.keyword = keyword
		const data = await pcsService.getProducts(filterPayload.value)
		rawFilterData.value = data.filter
		currentProductList.value =
			page === 1 ? data.groups : [...currentProductList.value, ...data.groups]
		isShowMore.value = data.meta.has_next_page
		totalResults.value = data.meta.total_count
		setFilterData()
	} catch (error) {
		console.error(error)
	} finally {
		isLoading.value = false
	}
}

const clearAllFilters = () => {
	// A helper function to clear the selected status of CheckboxItems
	const clearCheckboxItems = (items: CheckboxItem[]) => {
		items.forEach(item => (item.selected = false))
	}

	;[
		brandList,
		colorList,
		osList,
		storageList,
		seriesList,
		manufactList,
	].forEach(list => clearCheckboxItems(list.value))

	// For locationList, we need to clear the nested items
	locationList.value.forEach(locationItem =>
		clearCheckboxItems(locationItem.items),
	)

	minPrice.value = 0
	maxPrice.value = defaultMaxPrice.value
}

export function usePCSFilter() {
	return {
		rawFilterData,
		checkboxTitles,
		brandList,
		locationList,
		colorList,
		osList,
		storageList,
		seriesList,
		manufactList,
		currentFilterOptions,
		currentProductList,
		AttributeID,
		isShowMore,
		currentPage,
		minPrice,
		maxPrice,
		totalResults,
		defaultMaxPrice,
		currentFilterAmount,
		isLoading,
		isInited,
		getProductData,
		updateProductData,
		clearAllFilters,
		addFilterOptionsToUrl,
		setFilterPayloadFromUrl,
	}
}
